import React, { useRef, useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router-dom";

// Mocking external imports for the standalone file
// In your project, keep your existing imports for emailjs, BlueHeader, and images
const emailjs = {
  sendForm: () => Promise.resolve({ text: "Success" })
};

/**
 * Form Component 
 * Handles contact form validation and submission logic
 */
const Form = () => {
  const form = useRef();

  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    message: "",
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.user_name.trim()) {
      newErrors.user_name = "Name is required";
    } else if (data.user_name.trim().length < 2) {
      newErrors.user_name = "Name must be at least 2 characters";
    } else if (!/^[A-Za-z\s]+$/.test(data.user_name)) {
      newErrors.user_name = "Name must contain only letters and spaces";
    }

    if (!data.user_email.trim()) {
      newErrors.user_email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(data.user_email)) {
      newErrors.user_email = "Invalid email format";
    }

    if (!data.message.trim()) {
      newErrors.message = "Message is required";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };
    setFormData(updatedData);

    if (touched[name]) {
      setErrors(validateForm(updatedData));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
    setErrors(validateForm(formData));
  };

  const isFormValid = Object.keys(validateForm(formData)).length === 0;

  const sendEmail = (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    setTouched({
      user_name: true,
      user_email: true,
      message: true,
    });

    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    setStatusMessage("");

    // Using the parameters from your original code
    emailjs
      .sendForm("service_exhbi7l", "template_70mcyp9", form.current, {
        publicKey: "KQdugCwpjS3eVmO-b",
      })
      .then(() => {
        setStatusMessage("Message sent successfully!");
        form.current.reset();
        setFormData({
          user_name: "",
          user_email: "",
          message: "",
        });
        setTouched({});
        setErrors({});
      })
      .catch((error) => {
        console.error("EmailJS error:", error.text);
        setStatusMessage("Failed to send message. ❌ Please try again later.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="my-10 w-full max-w-lg bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-3xl font-semibold text-gray-800 mb-6">Contact Us</h2>

      <form ref={form} onSubmit={sendEmail} className="space-y-5">
        <div>
          <label htmlFor="name" className="block font-semibold text-gray-700 mb-1">
            Your Name<span className="text-red-500"> *</span>
          </label>
          <input
            type="text"
            id="name"
            name="user_name"
            value={formData.user_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your name"
            className={`w-full px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 ${
              errors.user_name && touched.user_name
                ? "ring-red-400"
                : "focus:ring-yellow-300"
            }`}
          />
          {errors.user_name && touched.user_name && (
            <p className="text-sm text-red-500 mt-1">{errors.user_name}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block font-semibold text-gray-700 mb-1">
            Your Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="user_email"
            value={formData.user_email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your email"
            className={`w-full px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 ${
              errors.user_email && touched.user_email
                ? "ring-red-400"
                : "focus:ring-yellow-300"
            }`}
          />
          {errors.user_email && touched.user_email && (
            <p className="text-sm text-red-500 mt-1">{errors.user_email}</p>
          )}
        </div>

        <div>
          <label htmlFor="message" className="block font-semibold text-gray-700 mb-1">
            Your Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your message"
            className={`w-full px-4 py-3 bg-gray-100 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 ${
              errors.message && touched.message
                ? "ring-red-400"
                : "focus:ring-yellow-300"
            }`}
          />
          {errors.message && touched.message && (
            <p className="text-sm text-red-500 mt-1">{errors.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className={`w-full flex justify-center items-center gap-2 bg-[#F7941D] text-gray-800 py-2 px-4 rounded-lg transition duration-300 ${
            isSubmitting || !isFormValid
              ? "opacity-60 cursor-not-allowed"
              : "hover:bg-yellow-400"
          }`}
        >
          {isSubmitting ? "Sending..." : "Send Message"}
        </button>

        {statusMessage && (
          <p className="mt-4 text-center text-sm text-gray-700" aria-live="polite">
            {statusMessage}
          </p>
        )}
      </form>
    </div>
  );
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

/**
 * Main Contact Component
 */
const Contact = ({ cameFromRoute = false }) => {
  const { ref, inView } = useInView({ threshold: 0.15 });
  const leftControls = useAnimation();
  const rightControls = useAnimation();
  
  // Checking current route to apply extra spacing
  const location = useLocation();
  const isContactUsRoute = location.pathname === "/ContactUs";

  useEffect(() => {
    if (inView) {
      leftControls.start("show");
      rightControls.start("show");
    } else {
      leftControls.start("hidden");
      rightControls.start("hidden");
    }
  }, [inView, leftControls, rightControls]);

  return (
    <section
      id="contact"
      className={`py-8 px-4 bg-gray-50 scroll-mt-24 ${
        (cameFromRoute || isContactUsRoute) ? "mt-[140px]" : ""
      }`}
    >
      <div ref={ref} className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left Text */}
          <motion.div
            variants={fadeInLeft}
            initial="hidden"
            animate={leftControls}
            className="space-y-6 p-6 text-center md:text-left"
          >
            <div className="inline-block text-left">
              <span className="text-[#F7941D] text-sm font-semibold block uppercase tracking-wider">
                CONNECT WITH US
              </span>
            </div>

            <div className="inline-block text-left">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 pb-2">
                Get in Touch With Us
              </h2>
              <div className="w-[130px] h-1 bg-[#F7941D]"></div>
            </div>

            {/* UPDATED CONTENT PARAGRAPH */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-xl mx-auto md:mx-0">
              We’re your trusted partner for innovative IT solutions across 
              development, cloud, AI, and more. Whether you have a project in 
              mind or simply want to explore how we can help, let’s connect.
            </p>
          </motion.div>

          {/* Right Form */}
          <motion.div
            variants={fadeInRight}
            initial="hidden"
            animate={rightControls}
            className="w-full flex justify-center md:justify-end"
          >
            <Form />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Default export as App for the preview environment
export default Contact;